import { BillingPrice, BillingProduct, BillingSubscription } from '@pollination-solutions/pollination-sdk'

export interface SelectedProduct {
  product: BillingProduct
  price: BillingPrice
  meta?: Record<string, any>
}

// Currency formatting helpers
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  }
  return symbols[currencyCode] || currencyCode
}

export const formatCurrency = (amount: string | number, currencyCode: string): string => {
  let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  numAmount = numAmount / 100 // Convert cents to dollars
  const symbol = getCurrencySymbol(currencyCode)
  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return `${symbol}${formatted}`
}

// Helper to determine if a price is monthly or yearly based on billing cycle
export const getPricePeriod = (price: BillingPrice): 'monthly' | 'yearly' => {
  const interval = price.billing_cycle?.interval?.toLowerCase() || 'month'
  return interval.includes('year') ? 'yearly' : 'monthly'
}


// Group prices by billing period
export const groupPricesByPeriod = (prices: BillingPrice[]) => {
  const monthly: BillingPrice[] = []
  const yearly: BillingPrice[] = []

  prices.forEach(price => {
    if (getPricePeriod(price) === 'monthly') {
      monthly.push(price)
    } else {
      yearly.push(price)
    }
  })

  return { monthly, yearly }
}

// Extract plan_multiplier from price data
export const getPlanMultiplier = (price: BillingPrice): number => {
  return price.custom_data?.['plan_multiplier'] || 1
}

// Determine resource type based on product name
export const getResourceType = (product: BillingProduct) => {
  const productName = product.name.toLowerCase()

  if (productName.includes('seat')) {
    return { type: 'seat', singular: 'organization seat', plural: 'organization seats' }
  } else if (productName.includes('rhino') && !productName.includes('bundled')) {
    return { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
  } else if (productName.includes('revit') && !productName.includes('bundled')) {
    return { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
  } else if (productName.includes('bundled')) {
    return { type: 'bundled_license', singular: 'bundled plugin license', plural: 'bundled plugin licenses' }
  } else {
    return { type: 'resource', singular: 'resource', plural: 'resources' }
  }
}

// Check if the selected billing period conflicts with existing subscriptions
export const hasConflictingBillingPeriod = (
  selectedPeriod: 'monthly' | 'yearly',
  existingSubscriptions: BillingSubscription[]
): {
  hasConflict: boolean
  conflictingSubscription?: BillingSubscription
} => {
  const periodMap = {
    'monthly': 'month',
    'yearly': 'year'
  }
  // Find active subscriptions with conflicting billing periods
  const conflictingSubscription = existingSubscriptions.find(sub =>
    sub.status === 'active'
    && sub.billing_cycle?.interval === periodMap[selectedPeriod] // Only check active subscriptions
  )

  return {
    hasConflict: !!conflictingSubscription,
    conflictingSubscription
  }
}

// Check if selected products conflict with existing subscription products
export const hasConflictingProducts = (
  selectedProducts: SelectedProduct[],
  existingSubscriptions: BillingSubscription[]
): {
  hasConflict: boolean
  conflictingProducts: string[]
} => {
  const conflictingProducts: string[] = []

  selectedProducts.forEach(selectedItem => {
    const productId = selectedItem.product.id
    const productName = selectedItem.product.name

    // Check if any active subscription already includes this product
    const isAlreadySubscribed = existingSubscriptions.some(subscription =>
      subscription.status === 'active' &&
      subscription.items?.some(item => item.product?.id === productId)
    )

    if (isAlreadySubscribed) {
      conflictingProducts.push(productName)
    }
  })

  return {
    hasConflict: conflictingProducts.length > 0,
    conflictingProducts
  }
}

export const getProductGroups = (
  billingProducts: BillingProduct[],
  existingSubscriptions: BillingSubscription[],
  billingPeriod: 'monthly' | 'yearly'
) => {
  const periodMap = {
    'monthly': 'month',
    'yearly': 'year'
  }

  const sortByUnitPriceAsc = (prices: BillingPrice[]) =>
    [...prices].sort(
      (a, b) =>
        parseFloat(a.unit_price?.amount ?? '0') -
        parseFloat(b.unit_price?.amount ?? '0')
    )

  return (() => {
    const flatList = billingProducts.flatMap(product => {
      const { monthly, yearly } = groupPricesByPeriod(product.prices ?? [])
      const periodPrices = billingPeriod === 'monthly' ? monthly : yearly
      const sortedPrices = sortByUnitPriceAsc(periodPrices)

      const isSeatProduct = product.plan_type === 'seat'
      const hasYearly = yearly.length > 0

      return sortedPrices.map(price => ({
        product,
        price,
        meta: {
          hasYearly,
          isSeatProduct
        }
      }))
    })


    // Seat should be free from conflicts
    const seatProducts = flatList
      .filter(item => item.meta.isSeatProduct)
      .sort(
        (a, b) =>
          parseFloat(a.price.unit_price?.amount ?? '0') -
          parseFloat(b.price.unit_price?.amount ?? '0')
      )

    const softwareProducts = flatList
      .filter(item => !item.meta.isSeatProduct)
      .map(item => {
      // Exclude seat products if there is an existing active subscription
      const isSubscribed = existingSubscriptions.some(sub => 
        sub.status === 'active' &&
        sub.billing_cycle?.interval === periodMap[billingPeriod] &&
        sub.items?.some(existing => item.product?.id === existing.product?.id)
      )
      return {
        ...item,
        meta: {
          ...item.meta,
          hasConflict: isSubscribed
        }
      }
    })
      .sort(
        (a, b) =>
          parseFloat(a.price.unit_price?.amount ?? '0') -
          parseFloat(b.price.unit_price?.amount ?? '0')
      )

    return { softwareProducts, seatProducts }
  })()
}
